/** eslint-disable no-undef */
import {
    Component,
    Host,
    Prop,
    h,
    Element,
    forceUpdate,
    State,
} from "@stencil/core";
import { state } from "../stores/notification-handler-store";
import { state as userSettingsState } from "../stores/user-settings-handler-store";
import { AuthSubscribe, AuthUnsubscribe, loadMore } from "../auth/auth";
import { translations } from "./i18n";
import { i18nProvider } from "@trumpf/ux.i18n";
import striptags from "striptags";
import { marked } from "marked";
import dompurify from "dompurify";
import { getHumanTime } from "../../utils/getHumanTime";

const { t } = i18nProvider;

enum settingsENV {
    dev = "https://trumpf.com/go/notification/settings/",
    qa = "https://trumpf.com/go/notification/settings/",
    prod = "https://trumpf.com/go/notification/settings/",
}

@Component({
    tag: "uxn-notification-center",
    styleUrl: "notification-center.scss",
    shadow: false,
})
export class NotificationCenter {
    private baseClass: string = "uxn-notification-center";

    @Element() domNode: HTMLUxnNotificationCenterElement;

    /** the url to the main notification center */
    @Prop() notificationsCenterUrl: string = "";
    /** the url to the notification setting */
    @Prop() notificationCenterTarget: string = "_blank";
    /** the headline of the notifications layer */
    @Prop() notificationSettingsUrl: string = "";
    /** target of the notifications setting link */
    @Prop() notificationSettingsTarget: string = "";
    /** target of the notifications setting link */
    @Prop() settingsDisabled: boolean = false;
    /** backend api version  */
    @Prop() version: string = "2.0";
    /** environment for fetching data  */
    @Prop() env: string = "prod";
    /** date format for notifications */
    @Prop() dateFormat: string = "DD-MM-YYYY";
    /** time format for notifications */
    @Prop() timeFormat: string = "HH:mm";
    /** language code  */
    @Prop() locale: string = "de_INT";
    /** debugging */
    @Prop() debug: boolean = false;

    @State() retry: number = 0;
    @State() settingsURL: string;

    private subscription: number;
    private i18nSubscriberId: string;

    private customTranslation() {
        const { i18n } = i18nProvider;
        Object.keys(translations).forEach((key) =>
            i18n.addResourceBundle(key, "notifications", translations[key])
        );

        /**
         * Handles language changes in the i18n instance, and falls back to a
         * related locale if the exact language isn't available in the translations.
         */
        i18n.on("languageChanged", (lng) => {
            if (!Object.keys(translations).includes(lng)) {
                const fallbackLng = lng.split("_")[0];
                const fallbackLocale = Object.keys(translations).find((key) =>
                    key.startsWith(fallbackLng)
                );
                if (typeof fallbackLocale !== "undefined") {
                    i18n.changeLanguage(fallbackLocale);
                }
            }
        });
    }

    componentWillLoad() {
        this.settingsURL =
            this.notificationSettingsUrl || settingsENV[this.env];
    }

    componentDidLoad() {
        AuthSubscribe(this.version, this.env, this.locale, this.debug);
        this.customTranslation();
        i18nProvider.changeLanguage(this.locale || "de_DE");

        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
        state.onChange("notifications", () => {
            this.resetInfiniteScroll();
        });
    }

    private resetInfiniteScroll = (): void => {
        const infiniteScrollSensor = this.domNode.querySelector<any>(
            "ux-infinite-scroll-sensor"
        );
        if (infiniteScrollSensor === null) return;
        infiniteScrollSensor.hasFired = false;
    };

    disconnectedCallback() {
        AuthUnsubscribe(this.subscription);
        i18nProvider.unsubscribe(this.i18nSubscriberId);
        state.set("token", null);
    }

    private clusterDate = (type: string, date) => {
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);

        const dateString = new Date(date).toDateString();

        if (type === "today") {
            return dateString === today.toDateString();
        }
        if (type === "yesterday") {
            return dateString === yesterday.toDateString();
        }
        if (type === "week") {
            return (
                dateString !== today.toDateString() &&
                dateString !== yesterday.toDateString()
            );
        }

        return false;
    };

    private renderNotificationCluster = (type: string, headline: string) => {
        const filteredNotifications = state
            .get("notifications")
            .filter((notificationData) =>
                this.clusterDate(type, notificationData.timestamp * 1000)
            );

        if (filteredNotifications.length === 0) {
            return null;
        }

        return (
            <div
                class={`${this.baseClass}__cluster-container ${this.baseClass}__cluster-container--${type}`}
            >
                {filteredNotifications.map((notificationData, index) => (
                    <div
                        id={"notification-" + index}
                        class={`${this.baseClass}__notification-cluster-item`}
                    >
                        <div class={`${this.baseClass}__cluster-headline`}>
                            <ux-tag>{headline}</ux-tag>
                        </div>
                        {this.renderNotificationItem(notificationData)}
                    </div>
                ))}
            </div>
        );
    };

    private renderNotificationItem = (data): HTMLDivElement => {
        const { t } = i18nProvider;
        let message =
            data.category == "system-generated"
                ? t("notifications:newCategoryAvailable").replace(
                      /{{category}}/g,
                      data.message
                  )
                : data.message;
        const renderer = new marked.Renderer();
        renderer.link = (href, title, text) => {
            return `<a target="_blank" href="${href}"${
                Boolean(title) ? ` title="${title}"` : ""
            }>${text}</a>`;
        };
        renderer.paragraph = (text) => {
            return `<p style="margin: 0; padding: 0;">${text}</p>`;
        };
        message = marked.parse(message, { renderer });
        message = striptags(message, ["a"]);
        message = dompurify.sanitize(marked.parse(message, { renderer }), {
            ADD_ATTR: ["target"],
        });

        const willDeleteIn = getHumanTime((data.timestamp + data.ttl) * 1000);

        return (
            <uxn-notification-item
                source={
                    userSettingsState
                        .get("categories")
                        ?.categories?.find(
                            (category) => category.name == data.category
                        )?.title
                }
                headline={data.subject}
                createdAt={data.timestamp}
                dateFormat={this.dateFormat}
                timeFormat={this.timeFormat}
                severity={data.severity}
                type="Overview"
                willDeleteIn={willDeleteIn}
                willDeleteInTooltip={t("notifications:autoDeleteAfterTime")}
            >
                <span slot="message" innerHTML={message} />
            </uxn-notification-item>
        );
    };

    render() {
        return (
            <Host class={this.baseClass}>
                {userSettingsState.get("loading") == true ||
                state.get("loading") == true ? (
                    <div class={`${this.baseClass}__loading-ct`}>
                        <ux-spinner-overlay opened={true} />
                    </div>
                ) : (
                    <div>
                        <div class={`${this.baseClass}__header`}>
                            {this.settingsURL &&
                                this.settingsDisabled !== true && (
                                    <a
                                        href={this.settingsURL}
                                        target={this.notificationSettingsTarget}
                                    >
                                        <ux-icon
                                            id="notificationSettingsIcon"
                                            name="cog"
                                            weight="bold"
                                        />
                                    </a>
                                )}
                            <button class="button-close" data-ux-tooltip-close>
                                <ux-icon name="close" />
                            </button>
                        </div>
                        {state.get("error") !== null ? (
                            <div class={`${this.baseClass}__error`}>
                                <div class={`${this.baseClass}__error-message`}>
                                    {t("notifications:error")}
                                </div>
                            </div>
                        ) : state.get("token") !== null ? (
                            state.get("counter") > 0 ? (
                                <div
                                    class={`${this.baseClass}__notification-container`}
                                >
                                    {this.renderNotificationCluster(
                                        "today",
                                        t("notifications:todayLabel")
                                    )}
                                    {this.renderNotificationCluster(
                                        "yesterday",
                                        t("notifications:yesterdayLabel")
                                    )}
                                    {this.renderNotificationCluster(
                                        "week",
                                        t("notifications:lastSevenDays")
                                    )}
                                    {this.renderNotificationCluster(
                                        "olderThanAWeek",
                                        t("notifications:olderLabel")
                                    )}

                                    <ux-infinite-scroll-sensor
                                        onUxInfiniteScrollSensorScrolledIntoView={() =>
                                            loadMore()
                                        }
                                    />
                                </div>
                            ) : userSettingsState.get("userSettings") &&
                              userSettingsState
                                  .get("userSettings")
                                  .channels.find(
                                      (channel) =>
                                          channel.name == "notificationChannel"
                                  ).enabled == true ? (
                                userSettingsState.get("userSettings") &&
                                !userSettingsState
                                    .get("categories")
                                    .categories.some((c) =>
                                        userSettingsState
                                            .get("userSettings")
                                            .categories.some(
                                                (uc) => uc.name === c.name
                                            )
                                    ) ? (
                                    <div class={`${this.baseClass}__empty`}>
                                        <ux-icon
                                            class={`${this.baseClass}__empty-icon`}
                                            name="no-notification"
                                        />
                                        <p
                                            class={`${this.baseClass}__empty-info`}
                                        >
                                            {t(
                                                "notifications:selectCategoryInSettings"
                                            )}
                                        </p>
                                    </div>
                                ) : (
                                    <div class={`${this.baseClass}__empty`}>
                                        <ux-icon
                                            class={`${this.baseClass}__empty-icon`}
                                            name="no-notification"
                                        />
                                        <p
                                            class={`${this.baseClass}__empty-info`}
                                        >
                                            {t("notifications:noMessages")}
                                        </p>
                                    </div>
                                )
                            ) : (
                                <div class={`${this.baseClass}__empty`}>
                                    <ux-icon
                                        class={`${this.baseClass}__empty-icon`}
                                        name="no-notification"
                                    />
                                    <p class={`${this.baseClass}__empty-info`}>
                                        {t("notifications:activateInSettings")}
                                    </p>
                                </div>
                            )
                        ) : (
                            <div class={`${this.baseClass}__error`}>
                                <div class={`${this.baseClass}__error-message`}>
                                    {t("notifications:loginToView")}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Host>
        );
    }
}
